/**
 * 공정 흐름도(물질·에너지수지) 화면.
 *
 * 노드를 추가·연결해 소스→싱크 유향비순환그래프를 만들고, 계산 결과를
 * 물질·에너지수지표로 보여준다. 계산은 `flowsheetState.ts`/
 * `processNetwork.ts` 가 한다.
 */
import { useMemo } from "react";
import { PIPE_ROUGHNESS_M } from "../calc/pipeFlow";
import { mixComponentProperties, moleFractionsToMassComponents } from "../calc/gasComposition";
import type { ReductionSuccess, EsfOffgasSuccess } from "../calc/ironReduction";
import { PIPE_SIZES, nearestSchedule, schedulesFor } from "../data/pipes";
import { GAS_COMPONENTS } from "../data/gasComponents";

/** 물(H2O)은 스크러버 노드가 waterVaporMassFlowKgH 로 따로 추적하므로,
 * 여기 성분 선택지에서는 제외한다 — 안 그러면 성분으로도 넣고 수분으로도
 * 잡혀 이중계산된다. */
const FLOWSHEET_GAS_COMPONENTS = GAS_COMPONENTS.filter((c) => c.key !== "water");
import { buildFlowsheetCsv, defaultFlowsheetCsvName, downloadCsv } from "./csvExport";
import { Field, Help, Item, num } from "./formBits";
import { MATERIAL_LABELS, type PipeMaterial } from "./pipingState";
import { formatFromKpa, gaugeLabel, type PressureUnit } from "./pressureUnit";
import {
  NODE_KIND_LABELS,
  newFlowsheetNode,
  outputPointsOf,
  referenceOptions,
  removeFlowsheetNode,
  runFlowsheet,
  type FlowsheetState,
  type GasComponentEntryForm,
  type NodeKind,
} from "./flowsheetState";

const NODE_KINDS: readonly NodeKind[] = [
  "source",
  "sink",
  "mixer",
  "splitter",
  "pipe",
  "heater",
  "scrubber",
  "valve",
  "reductionFurnace",
  "esfOffgas",
];

function dryGasMassFlowKgH(components: Readonly<Record<string, number>>): number {
  return Object.values(components).reduce((a, b) => a + b, 0);
}

function fmt(v: number, digits = 1): string {
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString("ko-KR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function FlowsheetPanel({
  value,
  unit,
  onChange,
}: {
  value: FlowsheetState;
  unit: PressureUnit;
  onChange: (next: FlowsheetState) => void;
}) {
  const patchNode = (id: string, patch: Partial<FlowsheetState["nodes"][number]>) =>
    onChange({
      nodes: value.nodes.map((n) => (n.id === id ? { ...n, ...patch } : n)),
    });

  const result = useMemo(() => runFlowsheet(value, unit), [value, unit]);

  return (
    <>
      <section className="card no-print">
        <div className="row-head">
          <h2>노드</h2>
          <select
            value=""
            onChange={(e) => {
              const k = e.target.value as NodeKind;
              if (k) {
                onChange({ nodes: [...value.nodes, newFlowsheetNode(k, value.nodes)] });
              }
            }}
          >
            <option value="">+ 노드 추가…</option>
            {NODE_KINDS.map((k) => (
              <option key={k} value={k}>
                {NODE_KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </div>
        <p className="hint">
          <b>소스</b>(경계 유입)에서 시작해 배관·믹서·스플리터·히터·스크러버·
          밸브를 거쳐 <b>싱크</b>(경계 유출)로 끝나는 그물을 만드십시오.
          순환(리사이클)은 다루지 않습니다. 조성이 고정되고 상변화가 없는
          스트림만 정확합니다 — 물(수증기)의 응축·증발만 스크러버 노드가
          다룹니다.
        </p>

        {value.nodes.map((n) => (
          <NodeCard
            key={n.id}
            node={n}
            allNodes={value.nodes}
            unit={unit}
            reactionSummary={result.ok ? result.reactionSummaries.get(n.id) : undefined}
            onPatch={(patch) => patchNode(n.id, patch)}
            onRemove={() => onChange(removeFlowsheetNode(value, n.id))}
          />
        ))}
      </section>

      <section className="card sheet">
        <div className="row-head">
          <h2>물질·에너지수지표</h2>
          {result.ok && (
            <button
              type="button"
              className="btn no-print"
              onClick={() =>
                downloadCsv(
                  buildFlowsheetCsv(result, value.nodes, unit),
                  defaultFlowsheetCsvName(),
                )
              }
            >
              엑셀용 CSV 내보내기
            </button>
          )}
        </div>
        {!result.ok ? (
          <div className="result result--error">
            <p>
              <b>계산할 수 없습니다:</b> {result.message}
              {result.where && <span className="muted"> ({result.where})</span>}
            </p>
          </div>
        ) : (
          <>
            {result.warnings.length > 0 && (
              <ul className="warnings">
                {result.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            )}
            <FlowsheetStreamTable result={result} nodes={value.nodes} unit={unit} />
          </>
        )}
      </section>
    </>
  );
}

function FlowsheetStreamTable({
  result,
  nodes,
  unit,
}: {
  result: Extract<ReturnType<typeof runFlowsheet>, { ok: true }>;
  nodes: FlowsheetState["nodes"];
  unit: PressureUnit;
}) {
  const points = result.nodes
    .flatMap((n) => outputPointsOf(nodes.find((f) => f.id === n.id)!))
    .map((pt) => ({ pt, stream: result.streams.get(pt.id) }))
    .filter(
      (x): x is { pt: (typeof x)["pt"]; stream: NonNullable<(typeof x)["stream"]> } =>
        x.stream !== undefined,
    );

  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            <th>지점</th>
            {points.map((x) => (
              <th key={x.pt.id}>{x.pt.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>상태</td>
            {points.map((x) => (
              <td key={x.pt.id}>{x.stream.phase === "gas" ? "습윤가스" : "액체"}</td>
            ))}
          </tr>
          <tr>
            <td>건가스/액체 유량 (kg/h)</td>
            {points.map((x) => (
              <td key={x.pt.id}>
                {x.stream.phase === "gas"
                  ? fmt(dryGasMassFlowKgH(x.stream.components))
                  : fmt(x.stream.massFlowKgH)}
              </td>
            ))}
          </tr>
          <tr>
            <td>수분 유량 (kg/h)</td>
            {points.map((x) => (
              <td key={x.pt.id}>
                {x.stream.phase === "gas" ? fmt(x.stream.waterVaporMassFlowKgH) : "—"}
              </td>
            ))}
          </tr>
          <tr>
            <td>온도 (℃)</td>
            {points.map((x) => (
              <td key={x.pt.id}>{fmt(x.stream.temperatureK - 273.15)}</td>
            ))}
          </tr>
          <tr>
            <td>압력 ({gaugeLabel(unit)})</td>
            {points.map((x) => (
              <td key={x.pt.id}>
                {formatFromKpa(x.stream.pressureKpaAbs - 101.325, unit)}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function NodeCard({
  node: n,
  allNodes,
  unit,
  reactionSummary,
  onPatch,
  onRemove,
}: {
  node: FlowsheetState["nodes"][number];
  allNodes: FlowsheetState["nodes"];
  unit: PressureUnit;
  reactionSummary: ReductionSuccess | EsfOffgasSuccess | undefined;
  onPatch: (patch: Partial<FlowsheetState["nodes"][number]>) => void;
  onRemove: () => void;
}) {
  const refs = referenceOptions(allNodes, n.id);

  const InputSelect = ({ value: v, onChange: oc }: { value: string; onChange: (id: string) => void }) => (
    <select value={v} onChange={(e) => oc(e.target.value)}>
      <option value="">— 선택 —</option>
      {refs.map((r) => (
        <option key={r.id} value={r.id}>
          {r.label}
        </option>
      ))}
    </select>
  );

  return (
    <div className="scenario">
      <div className="scenario__head">
        <span className="muted">{NODE_KIND_LABELS[n.kind]}</span>
        <input value={n.label} onChange={(e) => onPatch({ label: e.target.value })} />
        <button type="button" className="btn btn--ghost" onClick={onRemove}>
          삭제
        </button>
      </div>

      {n.kind === "sink" && (
        <div className="grid-form">
          <Field label="입력" unit="" help="flowsheetInput">
            <InputSelect value={n.inputId} onChange={(id) => onPatch({ inputId: id })} />
          </Field>
        </div>
      )}

      {n.kind === "mixer" && (
        <div className="grid-form">
          {refs.map((r) => (
            <label key={r.id} className="scenario__check">
              <input
                type="checkbox"
                checked={n.inputIds.includes(r.id)}
                onChange={(e) =>
                  onPatch({
                    inputIds: e.target.checked
                      ? [...n.inputIds, r.id]
                      : n.inputIds.filter((x) => x !== r.id),
                  })
                }
              />
              {r.label}
            </label>
          ))}
        </div>
      )}

      {n.kind === "source" && (
        <>
          <div className="grid-form">
            <Field label="상태" unit="" help="flowsheetPhase">
              <select
                value={n.phase}
                onChange={(e) => onPatch({ phase: e.target.value as "gas" | "liquid" })}
              >
                <option value="gas">습윤가스</option>
                <option value="liquid">액체</option>
              </select>
            </Field>
            <Field label="온도" unit="K" help="flowsheetTemperature">
              <input value={n.temperatureK} onChange={(e) => onPatch({ temperatureK: e.target.value })} />
            </Field>
            <Field label="압력" unit={gaugeLabel(unit)} help="flowsheetPressure">
              <input value={n.pressureGauge} onChange={(e) => onPatch({ pressureGauge: e.target.value })} />
            </Field>
          </div>
          {n.phase === "gas" ? (
            <>
              <div className="grid-form">
                <Field label="조성 입력 방식" unit="" help="flowsheetCompositionMode">
                  <select
                    value={n.compositionMode}
                    onChange={(e) =>
                      onPatch({ compositionMode: e.target.value as "average" | "components" })
                    }
                  >
                    <option value="average">평균값 직접 입력</option>
                    <option value="components">성분 조성(몰%)으로 입력</option>
                  </select>
                </Field>
                <Field label="건가스 총 질량유량" unit="kg/h" help="flowsheetDryGasFlow">
                  <input
                    value={n.dryGasMassFlowKgH}
                    onChange={(e) => onPatch({ dryGasMassFlowKgH: e.target.value })}
                  />
                </Field>
                <Field label="수증기 질량유량" unit="kg/h" help="flowsheetWaterVapor">
                  <input
                    value={n.waterVaporMassFlowKgH}
                    onChange={(e) => onPatch({ waterVaporMassFlowKgH: e.target.value })}
                  />
                </Field>
                <Field label="압축계수 Z" unit="" help="compressibility">
                  <input
                    value={n.compressibility}
                    onChange={(e) => onPatch({ compressibility: e.target.value })}
                  />
                </Field>
              </div>

              {n.compositionMode === "average" ? (
                <div className="grid-form">
                  <Field label="건가스 분자량" unit="kg/kmol" help="molecularWeight">
                    <input
                      value={n.dryGasMolecularWeight}
                      onChange={(e) => onPatch({ dryGasMolecularWeight: e.target.value })}
                    />
                  </Field>
                  <Field label="건가스 비열" unit="J/kg·K" help="flowsheetDryGasCp">
                    <input
                      value={n.dryGasSpecificHeatJKgK}
                      onChange={(e) => onPatch({ dryGasSpecificHeatJKgK: e.target.value })}
                    />
                  </Field>
                  <Field label="점도" unit="cP" help="flowsheetViscosity">
                    <input
                      value={n.viscosityCp}
                      onChange={(e) => onPatch({ viscosityCp: e.target.value })}
                    />
                  </Field>
                </div>
              ) : (
                <GasCompositionEditor
                  entries={n.components}
                  totalMassFlowKgH={n.dryGasMassFlowKgH}
                  onChange={(components) => onPatch({ components })}
                />
              )}
            </>
          ) : (
            <div className="grid-form">
              <Field label="질량유량" unit="kg/h" help="flowMass">
                <input value={n.massFlowKgH} onChange={(e) => onPatch({ massFlowKgH: e.target.value })} />
              </Field>
              <Field label="밀도" unit="kg/m³" help="pdDensity">
                <input value={n.densityKgM3} onChange={(e) => onPatch({ densityKgM3: e.target.value })} />
              </Field>
              <Field label="비열" unit="J/kg·K" help="flowsheetLiquidCp">
                <input
                  value={n.specificHeatJKgK}
                  onChange={(e) => onPatch({ specificHeatJKgK: e.target.value })}
                />
              </Field>
              <Field label="점도" unit="cP" help="flowsheetViscosity">
                <input value={n.viscosityCp} onChange={(e) => onPatch({ viscosityCp: e.target.value })} />
              </Field>
            </div>
          )}
        </>
      )}

      {n.kind === "splitter" && (
        <>
          <div className="grid-form">
            <Field label="입력" unit="" help="flowsheetInput">
              <InputSelect value={n.inputId} onChange={(id) => onPatch({ inputId: id })} />
            </Field>
          </div>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>가지</th>
                  <th>방식</th>
                  <th>값</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {n.branches.map((b) => (
                  <tr key={b.id}>
                    <td>
                      <input
                        value={b.label}
                        onChange={(e) =>
                          onPatch({
                            branches: n.branches.map((x) =>
                              x.id === b.id ? { ...x, label: e.target.value } : x,
                            ),
                          })
                        }
                      />
                    </td>
                    <td>
                      <select
                        value={b.isFraction ? "frac" : "fixed"}
                        onChange={(e) =>
                          onPatch({
                            branches: n.branches.map((x) =>
                              x.id === b.id ? { ...x, isFraction: e.target.value === "frac" } : x,
                            ),
                          })
                        }
                      >
                        <option value="frac">분율(0~1)</option>
                        <option value="fixed">고정유량(kg/h)</option>
                      </select>
                    </td>
                    <td>
                      <input
                        value={b.value}
                        onChange={(e) =>
                          onPatch({
                            branches: n.branches.map((x) =>
                              x.id === b.id ? { ...x, value: e.target.value } : x,
                            ),
                          })
                        }
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        disabled={n.branches.length <= 2}
                        onClick={() =>
                          onPatch({ branches: n.branches.filter((x) => x.id !== b.id) })
                        }
                      >
                        삭제
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() =>
              onPatch({
                branches: [
                  ...n.branches,
                  {
                    id: `b_${n.id}_${n.branches.length}`,
                    label: `가지 ${n.branches.length + 1}`,
                    isFraction: true,
                    value: "0",
                  },
                ],
              })
            }
          >
            + 가지 추가
          </button>
        </>
      )}

      {n.kind === "pipe" && (
        <div className="grid-form">
          <Field label="입력" unit="" help="flowsheetInput">
            <InputSelect value={n.inputId} onChange={(id) => onPatch({ inputId: id })} />
          </Field>
          <Field label="호칭경" unit="in" help="pipeNps">
            <select
              value={n.nps}
              onChange={(e) =>
                onPatch({ nps: e.target.value, schedule: nearestSchedule(e.target.value, n.schedule) })
              }
              disabled={n.useCustomId}
            >
              {PIPE_SIZES.map((s) => (
                <option key={s.nps} value={s.nps}>
                  {s.nps}&quot;
                </option>
              ))}
            </select>
          </Field>
          <Field label="스케줄" unit="" help="pipeSchedule">
            <select
              value={n.schedule}
              onChange={(e) => onPatch({ schedule: e.target.value })}
              disabled={n.useCustomId}
            >
              {schedulesFor(n.nps).map((s) => (
                <option key={s} value={s}>
                  Sch {s}
                </option>
              ))}
            </select>
          </Field>
          <Field label="재질·조도" unit="" help="pipeMaterial">
            <select
              value={n.material}
              onChange={(e) => onPatch({ material: e.target.value as PipeMaterial })}
            >
              {(Object.keys(PIPE_ROUGHNESS_M) as PipeMaterial[]).map((m) => (
                <option key={m} value={m}>
                  {MATERIAL_LABELS[m]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="직관 길이" unit="m" help="straightLength">
            <input
              value={n.straightLengthM}
              onChange={(e) => onPatch({ straightLengthM: e.target.value })}
            />
          </Field>
          <Field label="부속 등가길이 합" unit="m" help="phFittingLength">
            <input
              value={n.fittingEquivalentLengthM}
              onChange={(e) => onPatch({ fittingEquivalentLengthM: e.target.value })}
            />
          </Field>
          <Field label="ΣK (액체 전용)" unit="" help="flowsheetFittingK">
            <input value={n.fittingK} onChange={(e) => onPatch({ fittingK: e.target.value })} />
          </Field>
          <label className="scenario__check">
            <input
              type="checkbox"
              checked={n.useCustomId}
              onChange={(e) => onPatch({ useCustomId: e.target.checked })}
            />
            내경 직접 입력
          </label>
          {n.useCustomId && (
            <Field label="내경" unit="mm" help="customInnerDiameter">
              <input value={n.customIdMm} onChange={(e) => onPatch({ customIdMm: e.target.value })} />
            </Field>
          )}
        </div>
      )}

      {n.kind === "heater" && (
        <div className="grid-form">
          <Field label="입력" unit="" help="flowsheetInput">
            <InputSelect value={n.inputId} onChange={(id) => onPatch({ inputId: id })} />
          </Field>
          <Field label="전열량 (+가열/−냉각)" unit="kW" help="flowsheetDuty">
            <input value={n.dutyKw} onChange={(e) => onPatch({ dutyKw: e.target.value })} />
          </Field>
        </div>
      )}

      {n.kind === "scrubber" && (
        <div className="grid-form">
          <Field label="입력 (습윤가스만)" unit="" help="flowsheetInput">
            <InputSelect value={n.inputId} onChange={(id) => onPatch({ inputId: id })} />
          </Field>
          <Field label="출구 온도" unit="K" help="flowsheetTemperature">
            <input
              value={n.outletTemperatureK}
              onChange={(e) => onPatch({ outletTemperatureK: e.target.value })}
            />
          </Field>
          <Field label="출구 압력" unit={gaugeLabel(unit)} help="flowsheetPressure">
            <input
              value={n.outletPressureGauge}
              onChange={(e) => onPatch({ outletPressureGauge: e.target.value })}
            />
          </Field>
          <Field label="포화도" unit="%" help="flowsheetSaturation">
            <input
              value={n.saturationPercent}
              onChange={(e) => onPatch({ saturationPercent: e.target.value })}
            />
          </Field>
          <Field label="보충수 공급 가능량" unit="kg/h" help="flowsheetMakeupWater">
            <input
              value={n.makeupWaterMassFlowKgH}
              onChange={(e) => onPatch({ makeupWaterMassFlowKgH: e.target.value })}
            />
          </Field>
        </div>
      )}

      {n.kind === "valve" && (
        <div className="grid-form">
          <Field label="입력" unit="" help="flowsheetInput">
            <InputSelect value={n.inputId} onChange={(id) => onPatch({ inputId: id })} />
          </Field>
          <Field label="압력강하 ΔP" unit="kPa" help="flowsheetValveDrop">
            <input
              value={n.pressureDropKpa}
              onChange={(e) => onPatch({ pressureDropKpa: e.target.value })}
            />
          </Field>
        </div>
      )}

      {n.kind === "reductionFurnace" && (
        <>
          <p className="hint">
            수소를 주 환원가스로 쓰는 유동/샤프트로 — 목표 금속화율을 입력하면
            소모되는 H2 와 새로 생기는 수증기(H2O)를 계산합니다. 반응열은
            계산하지 않습니다(조성 변화만 봅니다) — 출구 온도를 맞추려면
            뒤에 히터 노드를 이어 붙이십시오. 전기용융로(ESF) 쪽은 이 노드와
            이어지지 않는 별도 노드입니다.
          </p>
          <div className="grid-form">
            <Field label="입력 (H2 포함 가스)" unit="" help="flowsheetInput">
              <InputSelect value={n.inputId} onChange={(id) => onPatch({ inputId: id })} />
            </Field>
            <Field label="광석 중 Fe2O3 유량" unit="kg/h" help="flowsheetOreFe2O3">
              <input
                value={n.oreFe2O3MassFlowKgH}
                onChange={(e) => onPatch({ oreFe2O3MassFlowKgH: e.target.value })}
              />
            </Field>
            <Field label="목표 금속화율" unit="%" help="flowsheetTargetMetallization">
              <input
                value={n.metallizationPercent}
                onChange={(e) => onPatch({ metallizationPercent: e.target.value })}
              />
            </Field>
          </div>
          {reactionSummary && "achievedMetallizationPercent" in reactionSummary && (
            <dl className="result__grid">
              <Item label="필요 H2" value={`${fmt(reactionSummary.requiredH2MassFlowKgH)} kg/h`} />
              <Item
                label="실제 소모 H2"
                value={`${fmt(reactionSummary.consumedH2MassFlowKgH)} kg/h`}
              />
              <Item
                label="생성 수증기(H2O)"
                value={`${fmt(reactionSummary.producedH2OMassFlowKgH)} kg/h`}
              />
              <Item
                label="도달 금속화율"
                value={`${fmt(reactionSummary.achievedMetallizationPercent)} %`}
              />
            </dl>
          )}
        </>
      )}

      {n.kind === "esfOffgas" && (
        <>
          <p className="hint">
            전기용융로(ESF) — 유동로와 이어지지 않는 독립 노드입니다. 광석
            유량·(그 광석이 여기 들어오기 전 도달한) 금속화율을 다시 입력해
            잔여 미환원 FeO 양을 구하고, 공급 탄소량으로 FeO + C → Fe + CO
            반응의 CO 발생량을 계산합니다.
          </p>
          <div className="grid-form">
            <Field label="광석 중 Fe2O3 유량" unit="kg/h" help="flowsheetOreFe2O3">
              <input
                value={n.oreFe2O3MassFlowKgH}
                onChange={(e) => onPatch({ oreFe2O3MassFlowKgH: e.target.value })}
              />
            </Field>
            <Field label="도달 금속화율" unit="%" help="flowsheetAchievedMetallization">
              <input
                value={n.metallizationPercent}
                onChange={(e) => onPatch({ metallizationPercent: e.target.value })}
              />
            </Field>
            <Field label="공급 탄소량" unit="kg/h" help="flowsheetCarbonFeed">
              <input
                value={n.carbonMassFlowKgH}
                onChange={(e) => onPatch({ carbonMassFlowKgH: e.target.value })}
              />
            </Field>
            <Field label="off-gas 온도" unit="K" help="flowsheetTemperature">
              <input value={n.temperatureK} onChange={(e) => onPatch({ temperatureK: e.target.value })} />
            </Field>
            <Field label="off-gas 압력" unit={gaugeLabel(unit)} help="flowsheetPressure">
              <input
                value={n.pressureGauge}
                onChange={(e) => onPatch({ pressureGauge: e.target.value })}
              />
            </Field>
          </div>
          {reactionSummary && "coMassFlowKgH" in reactionSummary && (
            <dl className="result__grid">
              <Item
                label="미환원 FeO"
                value={`${fmt(reactionSummary.unreactedFeOMassFlowKgH)} kg/h`}
              />
              <Item
                label="필요 탄소"
                value={`${fmt(reactionSummary.requiredCarbonMassFlowKgH)} kg/h`}
              />
              <Item
                label="실제 반응 탄소"
                value={`${fmt(reactionSummary.consumedCarbonMassFlowKgH)} kg/h`}
              />
              <Item label="CO 발생량" value={`${fmt(reactionSummary.coMassFlowKgH)} kg/h`} />
            </dl>
          )}
        </>
      )}
    </div>
  );
}

function GasCompositionEditor({
  entries,
  totalMassFlowKgH,
  onChange,
}: {
  entries: readonly GasComponentEntryForm[];
  totalMassFlowKgH: string;
  onChange: (entries: readonly GasComponentEntryForm[]) => void;
}) {
  const derived = useMemo(() => {
    const components = moleFractionsToMassComponents(
      num(totalMassFlowKgH),
      entries.map((e) => ({ key: e.key, molePercent: num(e.molePercent) })),
    );
    return components === null ? null : mixComponentProperties(components);
  }, [entries, totalMassFlowKgH]);

  const molePercentSum = entries.reduce((a, e) => a + (num(e.molePercent) || 0), 0);

  return (
    <>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>성분</th>
              <th>몰%</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id}>
                <td>
                  <select
                    value={e.key}
                    onChange={(ev) =>
                      onChange(
                        entries.map((x) => (x.id === e.id ? { ...x, key: ev.target.value } : x)),
                      )
                    }
                  >
                    {FLOWSHEET_GAS_COMPONENTS.map((c) => (
                      <option key={c.key} value={c.key}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    value={e.molePercent}
                    onChange={(ev) =>
                      onChange(
                        entries.map((x) =>
                          x.id === e.id ? { ...x, molePercent: ev.target.value } : x,
                        ),
                      )
                    }
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    disabled={entries.length <= 1}
                    onClick={() => onChange(entries.filter((x) => x.id !== e.id))}
                  >
                    삭제
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button
        type="button"
        className="btn btn--ghost"
        onClick={() =>
          onChange([
            ...entries,
            { id: `gc_${Date.now()}_${entries.length}`, key: "nitrogen", molePercent: "0" },
          ])
        }
      >
        + 성분 추가
      </button>
      <p className={Math.abs(molePercentSum - 100) > 0.5 ? "hint hint--warn" : "hint"}>
        몰% 합계: {fmt(molePercentSum, 1)}% {Math.abs(molePercentSum - 100) > 0.5 && "— 100%에 맞추십시오"}
      </p>
      {derived && (
        <dl className="result__grid">
          <Item label="유도 분자량" value={`${fmt(derived.molecularWeight, 2)} kg/kmol`} />
          <Item label="유도 비열비 k" value={fmt(derived.specificHeatRatio, 3)} />
          <Item label="유도 비열" value={`${fmt(derived.specificHeatJKgK, 0)} J/kg·K`} />
          <Item label="유도 점도" value={`${fmt(derived.viscosityCp, 4)} cP`} />
        </dl>
      )}
    </>
  );
}
